import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getReproductionById, updateReproduction } from "../../../api/reproductionApi";
import { getAnimals } from "../../../api/animalsApi";
import { getStatus } from "../../../api/statusApi";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiSave, FiCalendar, FiFileText, FiTag, FiLoader } from "react-icons/fi";
import { GiCow } from "react-icons/gi";
import './editRepro.css';

const EditReproduction = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [animalId, setAnimalId] = useState("");
    const [dateVel, setDateVel] = useState("");
    const [dateSai, setDateSai] = useState("");
    const [dateGes, setDateGes] = useState("");
    const [description, setDescription] = useState("");
    const [statusId, setStatusId] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);

    const [animals, setAnimals] = useState([]);
    const [statuses, setStatuses] = useState([]);

    useEffect(() => {
        Promise.all([loadReproduction(), loadAnimals(), loadStatus()])
            .finally(() => setLoading(false));
    }, []);

    const loadReproduction = async () => {
        try {
            const r = await getReproductionById(id);
            setAnimalId(r.animal_id);
            setDateVel(r.date_velage);
            setDateSai(r.date_saille);
            setDateGes(r.date_gestation);
            setDescription(r.description);
            setStatusId(r.status_id);
        } catch (error) {
            console.error("Error loading reproduction", error);
        }
    };

    const loadAnimals = async () => {
        try {
            const res = await getAnimals();
            setAnimals(res);
        } catch (error) {
            console.error("Error loading animals", error);
        }
    };

    const loadStatus = async () => {
        try {
            const res = await getStatus();
            setStatuses(res);
        } catch (error) {
            console.error("Error loading statuses", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await updateReproduction(id, {
                animal_id: animalId,
                date_velage: dateVel || '',
                date_saille: dateSai || '',
                description,
                status_id: statusId,
            });
            navigate("/admin/reproduction");
        } catch (error) {
            console.error("Error updating reproduction", error);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="er-page">
                <div className="er-loading">
                    <FiLoader className="er-loading-icon" />
                    <span>{t("editRepro.loading") || "Loading…"}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="er-page">
            <button type="button" className="er-back-btn" onClick={() => navigate("/admin/reproduction")}>
                <FiArrowLeft /> {t("editRepro.back") || "Back"}
            </button>

            <div className="er-header">
                <div className="er-header-icon"><GiCow /></div>
                <div>
                    <h1 className="er-title">{t("editRepro.title") || "Edit Reproduction"}</h1>
                    <p className="er-subtitle">{t("editRepro.subtitle") || "Update reproduction record details"}</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="er-form">
                {/* Animal */}
                <div className="er-field er-full">
                    <label className="er-label"><GiCow className="er-label-icon" /> {t("editRepro.animal") || "Animal"}</label>
                    <select className="er-input" value={animalId} onChange={(e) => setAnimalId(e.target.value)}>
                        <option value="">-- {t("editRepro.choose") || "Choose"} --</option>
                        {animals.map((a) => (
                            <option key={a.id} value={a.id}>{a.nom} (#{a.numero})</option>
                        ))}
                    </select>
                </div>

                {/* Date Vêlage */}
                <div className="er-field">
                    <label className="er-label"><FiCalendar className="er-label-icon" /> {t("editRepro.calvingDate") || "Calving Date"}</label>
                    <input className="er-input" type="date" value={dateVel} onChange={(e) => setDateVel(e.target.value)} />
                </div>

                {/* Date Saillie */}
                <div className="er-field">
                    <label className="er-label"><FiCalendar className="er-label-icon" /> {t("editRepro.matingDate") || "Mating Date"}</label>
                    <input className="er-input" type="date" value={dateSai} onChange={(e) => setDateSai(e.target.value)} />
                </div>

                {/* Date Gestation */}
                <div className="er-field">
                    <label className="er-label"><FiCalendar className="er-label-icon" /> {t("editRepro.gestationDate") || "Gestation Date"}</label>
                    <input className="er-input" type="date" value={dateGes} onChange={(e) => setDateGes(e.target.value)} />
                </div>

                {/* Status */}
                <div className="er-field">
                    <label className="er-label"><FiTag className="er-label-icon" /> {t("editRepro.status") || "Status"}</label>
                    <select className="er-input" value={statusId} onChange={(e) => setStatusId(e.target.value)}>
                        <option value="">-- {t("editRepro.choose") || "Choose"} --</option>
                        {statuses.map((s) => (
                            <option key={s.id} value={s.id}>{s.titre}</option>
                        ))}
                    </select>
                </div>

                {/* Description */}
                <div className="er-field er-full">
                    <label className="er-label"><FiFileText className="er-label-icon" /> {t("editRepro.description") || "Description"}</label>
                    <textarea className="er-input er-textarea" value={description} onChange={(e) => setDescription(e.target.value)}></textarea>
                </div>

                {/* Submit */}
                <div className="er-full">
                    <button type="submit" className="er-submit-btn" disabled={submitting}>
                        {submitting ? <><FiLoader className="er-spin" /> {t("editRepro.saving") || "Saving…"}</> : <><FiSave /> {t("editRepro.save") || "Save Changes"}</>}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditReproduction;
